import { Link, useLocation } from "react-router-dom";
import { Menu } from "lucide-react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { useUpload } from "@/hooks/useUpload";
import { useTelemetry } from "@/hooks/useTelemetry";
import { useLinkWithParams } from "@/hooks/useNavigateKeepParams";
import kubrickLogo from "@/assets/kubrick-logo.png";
import { featureFlags } from "@/config/featureFlags";

const navLinks = [
  { to: "/", labelKey: "nav.upload", requiresUpload: false, requiresMapping: false },
  { to: "/mapping", labelKey: "nav.mapping", requiresUpload: true, requiresMapping: false },
  { to: "/data-quality", labelKey: "nav.dataQuality", requiresUpload: true, requiresMapping: true },
  {
    to: "/known-mappings",
    labelKey: "nav.knownMappings",
    requiresUpload: false,
    requiresMapping: false,
  },
  { to: "/analytics", labelKey: "nav.analytics", requiresUpload: false, requiresMapping: false },
  ...(featureFlags.schemaBuilder
    ? [
        {
          to: "/schema-builder",
          labelKey: "nav.schema-builder",
          requiresUpload: false,
          requiresMapping: false,
        },
      ]
    : []),
] as const;

export function Navbar() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { fileUploaded, mappingComplete } = useUpload();
  const { track } = useTelemetry();
  const linkTo = useLinkWithParams();

  const handleNavClick = (to: string, label: string) => {
    track("navigation_clicked", {
      destination: to,
      label,
      previousPage: location.pathname,
    });
  };

  const changeLanguage = (lng: string) => {
    track("language_changed", {
      previousLanguage: i18n.language,
      newLanguage: lng,
    });
    i18n.changeLanguage(lng);
  };

  return (
    <header className="sticky top-0 z-40 w-full" style={{ backgroundColor: "var(--color-nav-bg)" }}>
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6">
        {/* Logo */}
        <Link
          to={linkTo("/")}
          className="flex items-center gap-3"
          aria-label="Home"
          style={{ color: "var(--color-nav-text-active)" }}
          onClick={() => handleNavClick("/", "Home")}
        >
          <img src={kubrickLogo} alt="Kubrick" className="h-7 w-auto" />
          <span
            className="hidden sm:inline-block text-[0.65rem] font-medium uppercase tracking-[0.15em] opacity-60"
            style={{ color: "var(--color-nav-text)" }}
          >
            Klyra
          </span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-1">
          {navLinks.map((link) => {
            const isActive = location.pathname === link.to;
            const isDisabled =
              (link.requiresUpload && !fileUploaded) || (link.requiresMapping && !mappingComplete);
            const label = t(link.labelKey);

            if (isDisabled) {
              return (
                <span
                  key={link.to}
                  className="relative px-4 py-2 text-sm font-medium rounded-md text-[var(--color-nav-text)] opacity-30 cursor-not-allowed select-none"
                  title="Upload a file first"
                >
                  {label}
                </span>
              );
            }

            return (
              <Link
                key={link.to}
                to={linkTo(link.to)}
                onClick={() => handleNavClick(link.to, label)}
                className={cn(
                  "relative px-4 py-2 text-sm font-medium rounded-md transition-all duration-200",
                  isActive
                    ? "text-white bg-white/10"
                    : "text-[var(--color-nav-text)] hover:text-white hover:bg-white/[0.06]"
                )}
              >
                {label}
                <span
                  className={cn(
                    "absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 rounded-full bg-[var(--color-brand-primary)] transition-all duration-300",
                    isActive ? "w-6 opacity-100" : "w-0 opacity-0"
                  )}
                />
              </Link>
            );
          })}

          {/* Menu button */}
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button
                className="ml-2 p-2 rounded-md text-[var(--color-nav-text)] opacity-70 hover:text-white hover:opacity-100 hover:bg-white/[0.06] transition-colors"
                aria-label="Menu"
              >
                <Menu className="h-5 w-5" />
              </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content
                className="z-50 min-w-[160px] rounded-lg border border-[var(--color-border)] bg-white p-1.5 shadow-lg animate-in fade-in-0 zoom-in-95"
                sideOffset={8}
                align="end"
              >
                <DropdownMenu.Label className="px-2 py-1.5 text-xs font-semibold text-[var(--color-text-tertiary)] uppercase tracking-wider">
                  Language
                </DropdownMenu.Label>
                {[
                  { code: "en", label: "English" },
                  { code: "fr", label: "Francais" },
                  { code: "de", label: "Deutsch" },
                ].map((lang) => (
                  <DropdownMenu.Item
                    key={lang.code}
                    className={cn(
                      "flex items-center rounded-md px-2 py-1.5 text-sm cursor-pointer outline-none",
                      i18n.language === lang.code
                        ? "bg-[var(--color-brand-surface)] text-[var(--color-brand-primary-dark)] font-medium"
                        : "text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)]"
                    )}
                    onSelect={() => changeLanguage(lang.code)}
                  >
                    {lang.label}
                  </DropdownMenu.Item>
                ))}
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        </nav>
      </div>
    </header>
  );
}
